<script setup>
import { useConfigStore } from '~/store'

const config = useConfigStore()
config.resetState()

useHead({ title: 'Privacy Policy' })
</script>

<template>
  <div class="flex flex-col max-w-3xl gap-4 p-4 pb-20 m-auto text-xs font-light sm:text-sm lg:text-base">
    <header>
      <h1 class="text-2xl text-green-400">
        Privacy Policy
      </h1>
    </header>

    <p>Date of last revision: September 23, 2022</p>

    <p>
      Rainberry, Inc. (the “Company" or “Rainberry”) is committed to respecting the privacy rights and concerns of all users of our websites ("Sites"),
      applications and services (collectively, the "Services"). As such, we have established and implemented this privacy policy to inform visitors
      to the Sites and users of the Services how we use and protect the information we collect. If you have any questions regarding this information
      or our privacy practices, please see the section entitled
      <a href="#section11" class="underline">"Questions? Contact Us"</a>
      at the end of this Privacy Policy.
    </p>

    <p>In this Privacy Policy, the Company’s software is referred to as the “RainberryTV” application or as the “Software”.</p>

    <h2 class="font-bold">
      Quick Links
    </h2>
    <ul class="list-disc">
      <li>
        <a href="#section1" class="underline">What information does Rainberry collect and how does Rainberry use it?</a>
      </li>
      <li>
        <a href="#section2" class="underline">Does Rainberry use "cookies"?</a>
      </li>
      <li>
        <a href="#section3" class="underline">Does Rainberry disclose the information it collects from its visitors/users to outside parties?</a>
      </li>
      <li>
        <a href="#section4" class="underline">How do you change your information and preferences?</a>
      </li>
      <li>
        <a href="#section5" class="underline">Third party links and co-brand relationships.</a>
      </li>
      <li>
        <a href="#section6" class="underline">Legal Basis For Processing Personal Information (EEA Visitors/Users Only)</a>
      </li>
      <li>
        <a href="#section7" class="underline">International Transfers.</a>
      </li>
      <li>
        <a href="#section8" class="underline">Data Retention (EEA Only)</a>
      </li>
      <li>
        <a href="#section9" class="underline">Your Data Protection Rights (EEA Visitors/Users Only)</a>
      </li>
      <li>
        <a href="#section10" class="underline">Notification of Changes.</a>
      </li>
      <li>
        <a href="#section11" class="underline">Questions? Contact us.</a>
      </li>
    </ul>

    <h2 id="section1" class="font-bold">
      What information does Rainberry collect and how does Rainberry use it?
    </h2>
    <h3 class="font-bold">
      Gathering and Use of Information About Your Browser and Device
    </h3>

    <p>
      When you come to one of our Sites or use our Services or Software, we may collect some limited information that your device
      and browser routinely make available whenever you visit a website or interact with any online service. This information
      your Internet Protocol (IP) address, port number, Software version, browser type, browser language, the date and time of
      your query and one or more cookies that may uniquely identify your browser. It may also include your approximate location,
      your device and operating system type and data from sensors in your device like the accelerometer.
    </p>
    <p>
      We collect this data to improve the overall quality of the online experience, including product monitoring, product improvement,
      and targeted advertising. Rainberry also uses such information for the following general purposes: to customize the advertising
      and content you see, fulfill your requests for products and services, improve our services, conduct research, and provide
      anonymous reporting for internal and external clients.
    </p>
    <p>
      In addition to collecting the above types of information, Rainberry collects queries for internal reporting and targeted advertising.
      We also count, track, and aggregate the visitor's activity into our analysis of general traffic flows at our Sites (e.g. tracking
      where traffic comes from, how traffic flows within the websites, etc.). To these ends, we may merge information about you into group
      data, which may then be shared on an aggregated basis with our advertisers.
    </p>
    <p>
      In the event that you provide us with answers to voluntary survey questions, we may present this information to our advertisers and
      partners, in the form of grouped statistics compiled from our users’ answers to such questions.
    </p>
    <p>
      We also collect data from the Software regarding traffic flows and content delivery performance as well as other data collected in
      the use of our products or services in order to understand usability and monitor network conditions and compare the performance of
      the Software and HTTP protocols on the public internet.
    </p>

    <h3 class="font-bold">
      Gathering and Use of Personal Information
    </h3>
    <p>
      You may visit the Sites and use the Software and many of the Services without any registration at all. However, from time to time,
      we may enable certain additional functionality or services whereby you will have to create a user account, and we may collect and
      process the following information about you:
    </p>
    <ul class="list-disc">
      <li>
        information such as your name, address, phone number and e-mail address, and other demographic information (such as date of birth,
        gender, age, and interests) that you provide by completing forms on the Sites, including if you register as a user of the Services,
        subscribe to our mailing lists, upload or submit any material through the services, or request any information.
      </li>
      <li>your log-in and password details in connection with the account sign-in process.</li>
      <li>
        if you log-in using a third party social media account (such as a Facebook or Google account), we may collect basic account
        information about you from that social media provider (such as your name, address, phone number and e-mail address, and other
        demographic information, like your date of birth, gender, age and interests).
      </li><li>
        details of any requests or transactions
        made by you through the Service.
      </li>
      <li>
        communications you send to us, for example to report a problem or to submit queries, concerns, or comments regarding
        the Service or its content.
      </li>
      <li>information that you post to the Site in the form of comments or contributions to discussions.</li>
    </ul>
    <p>We will use the personal information you provide to:</p>
    <ul class="list-disc">
      <li>identify you when you sign-in to your account.</li>
      <li>enable us to provide you with the Services or to carry out your instructions (for example, to process payment information).</li>
      <li>send you information we think you may find useful or which you have requested from us.</li>
      <li>
        present projects to you when you use the Service which we believe will be of interest based on your geographic location and previous
        use of the Service.
      </li>
      <li>administer your account with us.</li>
      <li>enable us to contact you regarding any question you make through the Service.</li>
      <li>analyze the use of the Service and the people visiting to improve our content and Services.</li>
      <li>use for other purposes that we may disclose to you when we request your information.</li>
    </ul>
    <p>
      We may also use e-mail addresses and other personal information from time to time in order to gather and provide to our members and
      visitors improved content and features.
    </p>

    <h2 id="section2" class="font-bold">
      Does Rainberry use "cookies"?
    </h2>
    <p>
      Cookies are small bits of electronic information that a web site can transfer to a visitor's hard drive to help tailor and keep records
      of his or her visit to the site. The use of cookies is standard on the Internet and many web sites use them. Cookies allow web site
      operators to better customize visits to the site to the visitor's individual preferences. For example, by setting a cookie you would
      not have to log in a password more than once, thereby saving time. We currently save password information in cookies. In addition,
      advertisements displayed on the Sites or in the Software may contain cookies. Third party analytics, ad-serving and affiliate partners
      may set or have cookies set and access their cookies or local storage objects (flash cookies) on your computer, when you are using the
      Software or visiting a Site and may collect information about your online activities across websites or online services. We may utilize
      Google Analytics Suite in the collection, compilation and analysis of information regarding visitor behavior and visitor demographics
      on some of our Services, and to develop website content. This analytics data is not tied to any Personal Information. For more
      information about Google Analytics, please visit www.google.com/policies/privacy/partners/. You can opt out of Google’s collection
      and Processing of data generated by your use of the Services by going to
      <a href="http://tools.google.com/dlpage/gaoptout" target="_blank" class="underline">http://tools.google.com/dlpage/gaoptout</a>.
      The third parties who serve our advertisements transfer cookies, and we do not control their use or content of their cookies and
      in many instances have no access to the information collected by them. Other companies' use of their cookies is subject to their
      own privacy policies, not this one. You should consult the respective privacy policies of these third-party ad servers or ad networks.
      This Privacy Policy does not apply to, and we cannot control the activities of, such other advertisers or web sites. If you do not wish
      to have this information used for the purpose of serving you targeted ads, please visit the suggested sites below for more information
      about the use of cookies and how to opt-out of them. Please note this does not stop you being served untargeted advertising. You will
      continue to receive generic ads, but these will no longer be tailored to your specific interests.
    </p>
    <p>
      <a href="http://www.pubmatic.com/privacy-policy.php" target="_blank" class="underline">http://www.pubmatic.com/privacy-policy.php</a>
    </p>
    <p><a href="http://www.indexexchange.com/privacy" target="_blank" class="underline">http://www.indexexchange.com/privacy</a></p>
    <p><a href="https://telaria.com/privacy-policy/" target="_blank" class="underline">https://telaria.com/privacy-policy/</a></p>
    <p><a href="https://www.sovrn.com/privacy-policy/" target="_blank" class="underline">https://www.sovrn.com/privacy-policy/</a></p>
    <p><a href="https://www.mopub.com/legal/privacy/" target="_blank" class="underline">https://www.mopub.com/legal/privacy/</a></p>
    <p><a href="https://www.appodeal.com/privacy-policy" target="_blank" class="underline">https://www.appodeal.com/privacy-policy</a></p>
    <p><a href="https://integralads.com/privacy-policy" target="_blank" class="underline">https://integralads.com/privacy-policy</a></p>
    <p><a href="http://www.pixalate.com/privacypolicy/" target="_blank" class="underline">http://www.pixalate.com/privacypolicy/</a></p>
    <p><a href="https://dev.adzerk.com/docs/privacy-policy-ad-server" target="_blank" class="underline">https://dev.adzerk.com/docs/privacy-policy-ad-server</a></p>
    <p><a href="https://liveramp.com/privacy/" target="_blank" class="underline">https://liveramp.com/privacy/</a></p>
    <p><a href="https://www.appnexus.com/platform-privacy-policy" target="_blank" class="underline">https://www.appnexus.com/platform-privacy-policy</a></p>
    <p><a href="http://optout.networkadvertising.org/?c=1#!/" target="_blank" class="underline">http://optout.networkadvertising.org/?c=1#!/</a></p>
    <p>
      Although most Web browsers automatically accept cookies, you can usually change your browser to prevent that or to notify you when you are
      sent a cookie. Even without accepting a cookie, you can still access many, if not all, of the features of the Rainberry websites or the
      Software. However, some parts of the Rainberry websites or the Software may not function properly if you refuse cookies.
    </p>

    <h2 id="section3" class="font-bold">
      Does Rainberry disclose the information it collects from its visitors to outside parties?
    </h2>
    <p>
      Except as provided herein, Rainberry does not sell, trade, or otherwise transfer outside the company personal information that
      visitors/users voluntarily provide Rainberry in any registration or use of the Sites or the Software, except to provide the
      Services (like when we partner with payment processors).
    </p>
    <p>
      This information we collect is used to better understand visitors’ and users' use of the features of the Sites or the Software,
      to support related transactions made on or through the Sites or the Software, and to better know our visitors/users and possibly
      tailor any specific features, promotions, advertisement or other notifications to them. However, this data in an aggregate form
      without any personal information may be provided to other parties for marketing, advertising, or other uses.
    </p>
    <p>
      Should you no longer wish to receive information from us, or should your information change, we have provided a way for you to
      correct, update or remove the personal data you gave us. You can do this simply by contacting us in any of the simple ways
      set forth below in the section entitled "How do you change your information and preferences?"
    </p>
    <p>
      We may disclose your personal information upon a transfer or sale to another entity of all or substantially all of our assets
      or equity securities or upon any other corporate reorganization.
    </p>
    <p>
      Notwithstanding any other terms of this Privacy Policy, we may release any personal information we obtain or collect when we
      believe its release is appropriate to comply with the law, enforce our Site or Software/Service policies, or protect ours or others'
      rights, property, or safety. For example, your personal information may be shared with any competent law enforcement body,
      regulatory, government agency, court or other third party (e.g. external legal advisors) where we believe disclosure is necessary.
      In addition, we may share your personal information to any person with your consent to the disclosure.
    </p>

    <h2 id="section4" class="font-bold">
      How do you change your information and preferences?
    </h2>
    <p>
      To update or modify the information and preferences, i.e., whether we share your personal information with third parties or
      publishers, or updating any marketing preferences we hold, if applicable, that we have on file for your user account,
      if applicable, simply go to the applicable Site or Service, log in, go to the user account section, then modify your details
      and settings as necessary.
    </p>

    <h2 id="section5" class="font-bold">
      Third party links and co-brand relationships.
    </h2>
    <p>
      In an attempt to provide increased value to our visitors/users, we may choose various third party web sites or services to link
      to, and/or frame within, the Sites or Services. We may also include offers from third parties as part of the installation process
      for our Software. We also participate in co-branding and other relationships to offer e-commerce and other services and features
      to our users. However, even if the third party is affiliated with us, we have no control over these third party sites or services,
      each of which has separate privacy and data collection practices independent of us. Data collected by our co-brand partners or
      third party web sites (even if offered on or through our Site or Services) may not be received by us. As such, we have no responsibility
      or liability for these independent policies or actions, and are not responsible for the privacy practices or the content of any such
      web sites or services. These linked sites or services are only for your convenience and you therefore access them at your own risk.
    </p>

    <h2 id="section6" class="font-bold">
      Legal Basis For Processing Personal Information (EEA Visitors/Users Only)
    </h2>
    <p>
      If you are a visitor/user from the European Economic Area (EEA), our legal basis for collecting and using the personal information
      described above will depend on the personal information concerned and the specific context in which we collect it.
    </p>
    <p>
      However, we will normally collect personal information from you only (i) where we need the personal information to perform a
      contract with you, (ii) where the processing is in our legitimate interests and not overridden by your rights, or (iii) where
      we have your consent to do so. Sometimes, we may have a legal obligation to collect personal information from you or may
      otherwise need the personal information to protect your vital interests or those of another person. In certain circumstances,
      we may collect your personal information if we need to perform a task carried out in public interest.
    </p>
    <p>
      If we ask you to provide personal information to comply with a legal requirement or to perform a contract with you, we will
      make this clear at the relevant time and advise you whether the provision of your personal information is mandatory or not
      (as well as of the possible consequences if you do not provide your personal information).
    </p>
    <p>
      If you are in the EEA, and we collect and use your personal information with your consent, then you can withdraw your consent,
      as envisaged in section
      <a href="#section9" class="underline">"Data protection rights of EEA visitors/users"</a> of this Privacy Policy.
    </p>
    <p>
      If we collect and use your personal information in reliance on our legitimate interests (or those of any third party),
      this interest will normally be to operate the Sites / the Software, communicating with you as necessary to provide the Services
      and/or the Software to you and for our legitimate commercial interest - for instance, when responding to your queries, improving
      the Software, the Services and/or the Sites, undertaking marketing, or for the purposes of detecting or preventing illegal activities.
      We may have other legitimate interests and if appropriate we will make clear to you at the relevant time what those legitimate
      interests are.
    </p>
    <p>
      We do not intend to collect and use sensitive personal information about you. However, if we collect and use sensitive personal
      information about you (e.g. health related data, biometric data, racial origin etc.), we will process such sensitive personal
      information, either to perform our legal and regulatory obligations, or on the basis of another lawful legal ground applicable
      for the processing of sensitive personal information (e.g., your explicit consent).
    </p>
    <p>
      If you have questions about or need further information concerning the legal basis on which we collect and use your personal
      information, please contact us by using the contact details provided under the
      <a href="#section11" class="underline"> "Questions? Contact us"</a> heading below.
    </p>

    <h2 id="section7" class="font-bold">
      International Transfers.
    </h2>
    <p>
      Rainberry, Inc. is a company headquartered in the United States of America. As such, when you use our Sites, Services or Software,
      your personal information may be transferred to the United States of America and other countries for processing. While data protection
      laws in these countries may not be as strict as the laws of the country in which you reside, your personal information will at all
      times be treated in accordance with this Privacy Policy.
    </p>

    <h2 id="section8" class="font-bold">
      Data Retention (EEA Only)
    </h2>
    <p>
      We retain personal information we collect from you where we have an ongoing legitimate business need to do so (for example,
      to provide you with the Services or to comply with applicable legal requirements).
    </p>
    <p>
      When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize it or,
      if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely
      store your personal information and isolate it from any further processing until deletion is possible.
    </p>

    <h2 id="section9" class="font-bold">
      Your Data Protection Rights (EEA Visitors/Users Only)
    </h2>
    <p>If you are in the EEA, you have the following data protection rights:</p>
    <p>
      If you wish to access, correct, update or request deletion of your personal information, you can do so at any time by contacting
      us using the contact details provided under the
      <a href="#section11" class="underline">"Questions? Contact us"</a> heading below.
    </p>
    <p>
      In addition, you can object to processing of your personal information, ask us to restrict processing of your personal information
      or request portability of your personal information. Again, you can exercise these rights by contacting us using the contact
      details provided under the
      <a href="#section11" class="underline">"Questions? Contact us"</a> heading below.
    </p>
    <p>
      You have the right to opt-out of marketing communications we send you at any time. You can exercise this right by clicking on
      the “unsubscribe” or “opt-out” link in the marketing e-mails we send you.
    </p>
    <p>
      Similarly, if we have collected and process your personal information with your consent, then you can withdraw your consent at any
      time by contacting us using the contact details provided under the
      <a href="#section11" class="underline">"Questions? Contact us"</a> heading below. Withdrawing
      your consent will not affect the lawfulness of any processing we conducted prior to your withdrawal, nor will it affect the
      processing of your personal information conducted in reliance on lawful processing grounds other than consent.
    </p>
    <p>
      You have the right to complain to the appropriate data protection authority about our collection and use of your personal information.
      For more information, please contact your local data protection authority (contact details for data protection authorities in the
      European Economic Area are available at
      <a href="https://ec.europa.eu/newsroom/article29/item-detail.cfm?item_id=612080" target="_blank" class="underline">
        https://ec.europa.eu/newsroom/article29/item-detail.cfm?item_id=612080</a>).
    </p>
    <p>
      If you wish to exercise the aforementioned rights and there is any question about who you are, we may require you to provide information
      from which we can satisfy ourselves as to your identity.
    </p>
    <p>
      We respond to all requests we receive from individuals wishing to exercise their data protection rights in accordance with applicable data protection laws.
    </p>

    <h2 id="section10" class="font-bold">
      Notification of Changes.
    </h2>
    <p>
      By visiting the website or using the applications and/or services, you consent to the collection and use of information by us as set
      forth in this privacy policy. If we decide to change our privacy policy, we will post those changes within this page so our users are
      always aware of what information we collect, how we use it, and under what circumstances, if any, we disclose it.
    </p>
    <p>You can see when this Privacy Policy was last updated by checking the “last updated” date displayed at the top of this Privacy Policy.</p>

    <h2 id="section11" class="font-bold">
      Questions? Contact us.
    </h2>
    <p>If you have any questions about our privacy practices, please do not hesitate to write to us at:</p>
    <section>
      <p>Rainberry Inc.</p><p>58 West Portal Ave., #552</p>
      <p>San Francisco, California 94127</p>
    </section>
  </div>
</template>
